import { useEffect, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Modal } from '@/components/Shared/Modal';
import { Button } from '@/components/Shared/Button';
import { Input } from '@/components/Shared/Input';
import { useMyVehicleTripsStore, type UnparkedTrip } from '@/store/useMyVehicleTripsStore';
import { formatKm } from '@/utils/distance';
import { formatDateTime, parseUtcTimestamp } from '@/utils/dateUtils';
import { MAX_ODOMETER_KM } from '@/utils/logbookLimits';
import { parseKm } from '@/utils/vehicleTrips';

interface ParkVehicleDialogProps {
  /** The trip to park; the dialog is closed without one. */
  trip: UnparkedTrip | null;
  onClose: () => void;
}

const DATE_TIME: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * "Fahrzeug abstellen" (FAFD-68): the driver enters the reading they left out when ending the trip.
 * Readings below the start or the last known reading are only flagged — the logbook marks
 * them for the administration. It can be entered once; afterwards only the administration
 * changes it.
 */
export function ParkVehicleDialog({ trip, onClose }: ParkVehicleDialogProps) {
  const { t } = useTranslation('vehicles');
  const park = useMyVehicleTripsStore((s) => s.park);
  const parking = useMyVehicleTripsStore((s) => s.parking);
  const [odometer, setOdometer] = useState('');

  useEffect(() => {
    if (trip) setOdometer('');
  }, [trip]);

  if (!trip) return null;

  const km = parseKm(odometer);
  const startKm = trip.startOdometerKm ?? null;
  const lastKm = trip.currentOdometerKm ?? null;
  const belowStart = km !== null && startKm !== null && km < startKm;
  // The last reading only adds something when it is not the trip's own start.
  const belowLast = km !== null && lastKm !== null && lastKm !== startKm && km < lastKm;

  const confirm = async () => {
    if (km === null || !trip.entryId) return;
    if (await park(trip.entryId, km)) onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('park.title', { plate: trip.licensePlate })}
      size="sm"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={parking}>
            {t('clock.cancel')}
          </Button>
          <Button onClick={confirm} disabled={parking || km === null}>
            {t('park.confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-[13px] text-slate-700 tabular-nums">
          {t('park.endedAt', { time: formatDateTime(parseUtcTimestamp(trip.endedAt), DATE_TIME) })}
          {startKm !== null && ` · ${t('park.startKm', { km: formatKm(startKm) })}`}
        </p>

        <div>
          <Input
            label={t('clock.odometerLabel')}
            inputMode="numeric"
            autoComplete="off"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))}
            placeholder={t('clock.odometerPlaceholder')}
            className="h-11 text-[15px] font-mono tabular-nums"
            error={odometer !== '' && km === null ? t('park.tooHigh', { km: formatKm(MAX_ODOMETER_KM) }) : undefined}
          />
          <span className="mt-1 block text-[12px] text-slate-500 tabular-nums">
            {lastKm !== null ? t('clock.lastOdometer', { km: formatKm(lastKm) }) : t('clock.noOdometer')}
          </span>
        </div>

        {(belowStart || belowLast) && (
          <p className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" strokeWidth={2} aria-hidden />
            <span>
              {belowStart
                ? t('park.belowStart', { km: formatKm(startKm) })
                : t('clock.odometerBelowLast', { km: formatKm(lastKm) })}
            </span>
          </p>
        )}

        {km !== null && startKm !== null && !belowStart && (
          <p className="text-[13px] text-slate-700 tabular-nums">
            {t('clock.drivenKm', { km: formatKm(km - startKm) })}
          </p>
        )}

        <p className="flex gap-2 text-[12px] text-slate-500">
          <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2} aria-hidden />
          <span>{t('park.onlyOnce')}</span>
        </p>
      </div>
    </Modal>
  );
}
